import type { NetworkQuality, NetworkQualityLevel } from '@/types'

/**
 * Visual mapping for the composite VoIP quality level emitted by the
 * agent's network_quality module. Centralised here so OperatorCard,
 * Realtime card border, and OperatorDetail network block stay in sync
 * — do not hard-code these values inline.
 */

export interface QualityVisual {
  /** Tremor color name (Badge / Text color prop) */
  tremor: 'green' | 'blue' | 'amber' | 'red' | 'gray'
  /** CSS color for inline styles (border, ring, dot) */
  cssBorder: string
  cssBg: string
  cssText: string
  /** Colored unicode dot rendered alongside the label */
  dot: string
}

const VISUALS: Record<NetworkQualityLevel, QualityVisual> = {
  excellent: {
    tremor: 'green',
    cssBorder: '#16a34a',  // tailwind green-600
    cssBg: 'rgba(22, 163, 74, 0.08)',
    cssText: '#15803d',
    dot: '🟢',
  },
  good: {
    tremor: 'blue',
    cssBorder: '#2563eb',  // tailwind blue-600
    cssBg: 'rgba(37, 99, 235, 0.08)',
    cssText: '#1d4ed8',
    dot: '🔵',
  },
  fair: {
    tremor: 'amber',
    cssBorder: '#d97706',  // tailwind amber-600
    cssBg: 'rgba(217, 119, 6, 0.08)',
    cssText: '#b45309',
    dot: '🟡',
  },
  poor: {
    tremor: 'red',
    cssBorder: '#dc2626',  // tailwind red-600
    cssBg: 'rgba(220, 38, 38, 0.08)',
    cssText: '#b91c1c',
    dot: '🔴',
  },
}

const UNKNOWN: QualityVisual = {
  tremor: 'gray',
  cssBorder: 'transparent',
  cssBg: 'transparent',
  cssText: 'inherit',
  dot: '⚪',
}

export function getQualityVisual(level: NetworkQualityLevel | null | undefined): QualityVisual {
  if (!level) return UNKNOWN
  return VISUALS[level] ?? UNKNOWN
}

export function getQualityLabel(nq: NetworkQuality | null | undefined): string {
  if (!nq) return 'Inconnue'
  return nq.level_label || 'Inconnue'
}
